import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { setUser } from "../../data/redux/authSlice";
import type { TenantOption } from "../../data/redux/authSlice";
import {
  getMyTenants,
  respondToInvite,
  switchTenant,
  type PendingInvite,
} from "../../services/tenantSwitchService";
import { isImpersonating } from "../../services/impersonationService";

// =============================================================================
// CompanySwitcher (CM-Central Guides/TENANCY-DESIGN.md §5.1)
//
// Shows which company the session is in. A login that belongs to several
// companies opens the most recently used one; this menu switches to another
// (a new token from the server, then a full reload — see switchTenant) and
// answers invitations to join a company.
//
// The company list is display-only: the server re-checks membership on every
// switch. Rendered as an <li> inside the header nav.
// =============================================================================

const CompanySwitcher: React.FC = () => {
  const { t } = useTranslation("header");
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.auth?.user);
  const tenantName: string | undefined = user?.tenantName;
  const tenantId: number | undefined = user?.tenantId;

  const [open, setOpen] = useState(false);
  const [tenants, setTenants] = useState<TenantOption[]>(Array.isArray(user?.tenants) ? user.tenants : []);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const containerRef = useRef<HTMLLIElement>(null);

  // Refresh from the server: the list cached at login goes stale when an
  // invite is accepted elsewhere (e.g. from the emailed link).
  const refresh = async () => {
    const mine = await getMyTenants();
    if (!mine) return;
    setTenants(mine.tenants);
    setInvites(mine.pendingInvites);
    if (user) {
      dispatch(setUser({ ...user, tenants: mine.tenants, pendingInviteCount: mine.pendingInvites.length }));
    }
  };

  useEffect(() => {
    if (!user?.isPlatformAdmin) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!tenantName || user?.isPlatformAdmin) return null;

  const otherTenants = tenants.filter((tn) => tn.tenantId !== tenantId);
  // An impersonation token can't be switched (the server refuses it too).
  const canSwitch = otherTenants.length > 0 && !isImpersonating();
  const hasMenu = canSwitch || invites.length > 0;

  const onSwitch = async (target: TenantOption) => {
    setBusy(`switch-${target.tenantId}`);
    try {
      await switchTenant(target.tenantId); // navigates away on success
    } catch (error: any) {
      toast.error(error?.message || t("company.switchFailed"));
      setBusy(null);
    }
  };

  const onRespond = async (invite: PendingInvite, accept: boolean) => {
    setBusy(`invite-${invite.token}`);
    try {
      await respondToInvite(invite.token, accept);
      toast.success(accept
        ? t("company.inviteAccepted", { company: invite.tenantName })
        : t("company.inviteDeclined"));
      await refresh();
    } catch (error: any) {
      toast.error(error?.message || t("company.inviteFailed"));
    } finally {
      setBusy(null);
    }
  };

  const itemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    border: "none",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    color: "#1e293b",
  };

  return (
    <li ref={containerRef} className="nav-item dropdown nav-item-box" style={{ position: "relative" }}>
      <button
        type="button"
        className="nav-link"
        onClick={(e) => {
          e.preventDefault();
          if (hasMenu) setOpen((v) => !v);
        }}
        title={hasMenu ? t("company.switchCompany") : t("company.label")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          fontWeight: 600,
          color: "#1e293b",
          background: "transparent",
          border: "none",
          cursor: hasMenu ? "pointer" : "default",
          maxWidth: 220,
        }}
      >
        <i className="ti ti-building" style={{ fontSize: 16, color: "#64748b" }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tenantName}</span>
        {invites.length > 0 && (
          <span className="badge rounded-pill bg-danger" style={{ fontSize: 10 }}>{invites.length}</span>
        )}
        {hasMenu && <i className="ti ti-chevron-down" style={{ fontSize: 14, color: "#64748b" }} />}
      </button>

      {hasMenu && (
        <div
          className={`dropdown-menu ${open ? "show" : ""}`}
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            left: "auto",
            marginTop: 8,
            minWidth: 260,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            boxShadow: "0 12px 36px rgba(0,0,0,0.12)",
            padding: 6,
            zIndex: 1050,
          }}
        >
          <div className="text-muted small px-2 pt-1 pb-1" style={{ fontWeight: 600 }}>
            {t("company.switchCompany")}
          </div>
          <div style={{ ...itemStyle, background: "#fff7ed", color: "#e85b28", cursor: "default" }}>
            <i className="ti ti-building" />
            <span style={{ flex: 1 }}>{tenantName}</span>
            <span style={{ fontSize: 11, textTransform: "uppercase" }}>{t("company.current")}</span>
          </div>
          {canSwitch && otherTenants.map((tn) => (
            <button
              key={tn.tenantId}
              type="button"
              className="dropdown-item"
              disabled={busy !== null}
              onClick={() => onSwitch(tn)}
              style={{ ...itemStyle, cursor: busy ? "wait" : "pointer" }}
            >
              <i className="ti ti-building" style={{ color: "#64748b" }} />
              <span style={{ flex: 1 }}>{tn.tenantName}</span>
              {busy === `switch-${tn.tenantId}` && <span className="spinner-border spinner-border-sm" role="status" />}
            </button>
          ))}

          {invites.length > 0 && (
            <>
              <div className="dropdown-divider" />
              <div className="text-muted small px-2 pb-1" style={{ fontWeight: 600 }}>
                {t("company.invitations")}
              </div>
              {invites.map((invite) => (
                <div key={invite.token} style={{ ...itemStyle, flexWrap: "wrap", cursor: "default" }}>
                  <i className="ti ti-mail" style={{ color: "#64748b" }} />
                  <span style={{ flex: 1 }}>{invite.tenantName}</span>
                  <span style={{ display: "flex", gap: 6 }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={busy !== null}
                      onClick={() => onRespond(invite, true)}
                    >
                      {t("company.accept")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      disabled={busy !== null}
                      onClick={() => onRespond(invite, false)}
                    >
                      {t("company.decline")}
                    </button>
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </li>
  );
};

export default CompanySwitcher;
